/** @format */

import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { productAction, updateProductAction } from "../../store/product/product.action";
import formatImageUrl, { formatAmount } from "../../extra/functions";
import defaultImage from "../../../assets/images/default.jpg";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

const saleTypeLabel = (type) => {
	switch (type) {
		case 1:
			return "Buy Now";
		case 2:
			return "Auction";
		case 3:
			return "Not for Sale";
		default:
			return "-";
	}
};

const ProductRequestView = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const { defaultCurrency } = useSelector((state) => state.currency);
	const symbol = defaultCurrency?.symbol || "";

	// state passed from list pages: { product, requestType ("Create" | "Update"), status ("Pending" | "Approved" | "Rejected") }
	const { product: data, requestType, status } = location.state || {};

	const [lightboxImg, setLightboxImg] = useState(null);
	const [activeImgIndex, setActiveImgIndex] = useState(0);
	const [showRejectModal, setShowRejectModal] = useState(false);
	const [rejectReason, setRejectReason] = useState("");
	const [actionLoading, setActionLoading] = useState(false);

	if (!data) {
		return (
			<div style={{ padding: "40px", textAlign: "center" }}>
				<p>No product data found.</p>
				<button style={styles.backBtn} onClick={() => navigate(-1)}>
					<ArrowBackIcon sx={{ fontSize: 18, marginRight: "4px" }} /> Go Back
				</button>
			</div>
		);
	}

	const allImages = [data.mainImage, ...(data.images || []).filter((i) => i !== data.mainImage)].filter(Boolean).map((img) => formatImageUrl(img));
	const sellerName = data.seller ? (typeof data.seller === "object" ? `${data.seller.firstName || ""} ${data.seller.lastName || ""}`.trim() || data.seller.businessName || data.seller.businessTag || "-" : data.seller) : "-";
	const categoryName = data.category ? (typeof data.category === "object" ? data.category.name : data.category) : "-";
	const subCategoryName = data.subCategory ? (typeof data.subCategory === "object" ? data.subCategory.name : data.subCategory) : "-";
	const currentStatus = data.createStatus || data.updateStatus || "-";
	const isPending = status === "Pending";

	const handleApprove = () => {
		setActionLoading(true);
		if (requestType === "Create") {
			dispatch(productAction(data._id, "Approved"));
		} else {
			dispatch(updateProductAction(data._id, "Approved"));
		}
		setTimeout(() => navigate(-1), 600);
	};

	const handleRejectConfirm = () => {
		if (!rejectReason.trim()) return;
		setActionLoading(true);
		if (requestType === "Create") {
			dispatch(productAction(data._id, "Rejected", rejectReason.trim()));
		} else {
			dispatch(updateProductAction(data._id, "Rejected", rejectReason.trim()));
		}
		setShowRejectModal(false);
		setTimeout(() => navigate(-1), 600);
	};

	return (
		<div style={styles.page}>
			{/* Top Bar */}
			<div style={styles.topBar}>
				<button style={styles.backBtn} onClick={() => navigate(-1)}>
					<ArrowBackIcon sx={{ fontSize: 18, marginRight: "4px" }} /> Back
				</button>
				<h5 style={styles.pageTitle}>Product Details</h5>
				{isPending && (
					<div style={styles.actionBtns}>
						<button style={styles.approveBtn} onClick={handleApprove} disabled={actionLoading}>
							<CheckCircleIcon sx={{ fontSize: 18, marginRight: "4px" }} /> Approve
						</button>
						<button style={styles.rejectBtn} onClick={() => setShowRejectModal(true)} disabled={actionLoading}>
							<CancelIcon sx={{ fontSize: 18, marginRight: "4px" }} /> Reject
						</button>
					</div>
				)}
			</div>

			{/* Status Banner */}
			<div
				style={{
					...styles.statusBanner,
					backgroundColor: currentStatus === "Pending" ? "#fff8e1" : currentStatus === "Approved" ? "#e8f5e9" : currentStatus === "Rejected" ? "#ffebee" : "#f5f5f5",
					borderColor: currentStatus === "Pending" ? "#ffca28" : currentStatus === "Approved" ? "#66bb6a" : currentStatus === "Rejected" ? "#ef5350" : "#e0e0e0",
					color: currentStatus === "Pending" ? "#f57f17" : currentStatus === "Approved" ? "#2e7d32" : currentStatus === "Rejected" ? "#c62828" : "#333",
				}}>
				<strong>Status:</strong> {currentStatus} &nbsp;|&nbsp; <strong>Request Type:</strong> {requestType === "Create" ? "New Product" : "Product Update"}
			</div>

			{/* Reject Reason Display */}
			{currentStatus === "Rejected" && data.rejectReason && (
				<div style={styles.rejectReasonBanner}>
					<strong>Rejection Reason:</strong> {data.rejectReason}
				</div>
			)}

			<div className='row' style={{ margin: 0 }}>
				{/* Left Column — Images */}
				<div className='col-lg-5 col-md-12' style={{ padding: "0 12px" }}>
					<div style={styles.section}>
						<h6 style={styles.sectionTitle}>
							<i className='bi bi-images me-2' style={{ color: "#e54a13" }}></i>
							Product Images
						</h6>
						{allImages.length > 0 ? (
							<>
								<div style={styles.mainImageWrap}>
									<img
										src={allImages[activeImgIndex]}
										alt='Product'
										style={styles.mainDisplayImg}
										onClick={() => setLightboxImg(allImages[activeImgIndex])}
										onError={(e) => {
											e.target.onerror = null;
											e.target.src = defaultImage;
										}}
									/>
								</div>
								{allImages.length > 1 && (
									<div style={styles.thumbnailRow}>
										{allImages.map((img, i) => (
											<img
												key={i}
												src={img}
												alt={`Thumb ${i + 1}`}
												style={{
													...styles.thumbnail,
													border: activeImgIndex === i ? "2px solid #e54a13" : "2px solid #e8e8e8",
												}}
												onClick={() => setActiveImgIndex(i)}
												onError={(e) => {
													e.target.onerror = null;
													e.target.src = defaultImage;
												}}
											/>
										))}
									</div>
								)}
							</>
						) : (
							<div style={styles.noImage}>No Images Available</div>
						)}
					</div>
				</div>

				{/* Right Column — Details */}
				<div className='col-lg-7 col-md-12' style={{ padding: "0 12px" }}>
					{/* Basic Info */}
					<div style={styles.section}>
						<h6 style={styles.sectionTitle}>
							<i className='bi bi-box-seam me-2' style={{ color: "#e54a13" }}></i>
							Product Information
						</h6>
						<div className='row'>
							<div className='col-md-6'>
								<InfoField label='Product Name' value={data.productName} />
							</div>
							<div className='col-md-6'>
								<InfoField label='Product Code' value={data.productCode} />
							</div>
							<div className='col-md-6'>
								<InfoField label='Sale Type' value={saleTypeLabel(data.productSaleType)} />
							</div>
							<div className='col-md-6'>
								<InfoField label='Status' value={currentStatus} badge />
							</div>
							<div className='col-md-6'>
								<InfoField label='Quantity' value={data.quantity != null ? data.quantity : "-"} />
							</div>
							<div className='col-md-6'>
								<InfoField label='Sold' value={data.sold != null ? data.sold : "-"} />
							</div>
							{data.tags && (
								<div className='col-12'>
									<InfoField label='Tags' value={data.tags} />
								</div>
							)}
							<div className='col-12'>
								<InfoField label='Description' value={data.description} />
							</div>
						</div>
					</div>

					{/* Pricing */}
					<div style={styles.section}>
						<h6 style={styles.sectionTitle}>
							<i className='bi bi-currency-dollar me-2' style={{ color: "#e54a13" }}></i>
							Pricing
						</h6>
						<div className='row'>
							<div className='col-md-4'>
								<InfoField label='Price' value={`${symbol}${formatAmount(data.price)}`} />
							</div>
							{data.salePrice != null && data.salePrice > 0 && (
								<div className='col-md-4'>
									<InfoField label='Sale Price' value={`${symbol}${formatAmount(data.salePrice)}`} />
								</div>
							)}
							<div className='col-md-4'>
								<InfoField label='Shipping Charges' value={`${symbol}${formatAmount(data.shippingCharges)}`} />
							</div>
							<div className='col-md-4'>
								<InfoField label='Allow Offer' value={data.allowOffer ? "Yes" : "No"} />
							</div>
							{data.allowOffer && (
								<div className='col-md-4'>
									<InfoField label='Minimum Offer Price' value={`${symbol}${formatAmount(data.minimumOfferPrice)}`} />
								</div>
							)}
							<div className='col-md-4'>
								<InfoField label='Immediate Payment Required' value={data.isImmediatePaymentRequired ? "Yes" : "No"} />
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Full Width Sections below */}
			<div style={{ padding: "0 12px" }}>
				{/* Auction Details */}
				{(data.enableAuction || data.productSaleType === 2) && (
					<div style={styles.section}>
						<h6 style={styles.sectionTitle}>
							<i className='bi bi-hammer me-2' style={{ color: "#e54a13" }}></i>
							Auction Details
						</h6>
						<div className='row'>
							<div className='col-md-3'>
								<InfoField label='Auction Enabled' value={data.enableAuction ? "Yes" : "No"} />
							</div>
							<div className='col-md-3'>
								<InfoField label='Starting Price' value={`${symbol}${formatAmount(data.auctionStartingPrice)}`} />
							</div>
							<div className='col-md-3'>
								<InfoField label='Duration (days)' value={data.auctionDuration || "-"} />
							</div>
							<div className='col-md-3'>
								<InfoField label='Reserve Price Enabled' value={data.enableReservePrice ? "Yes" : "No"} />
							</div>
							{data.enableReservePrice && (
								<div className='col-md-3'>
									<InfoField label='Reserve Price' value={`${symbol}${formatAmount(data.reservePrice)}`} />
								</div>
							)}
							<div className='col-md-3'>
								<InfoField label='Auction Start' value={data.auctionStartDate ? new Date(data.auctionStartDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "-"} />
							</div>
							<div className='col-md-3'>
								<InfoField label='Auction End' value={data.auctionEndDate ? new Date(data.auctionEndDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "-"} />
							</div>
						</div>
					</div>
				)}

				{/* Shipping & Processing */}
				<div style={styles.section}>
					<h6 style={styles.sectionTitle}>
						<i className='bi bi-truck me-2' style={{ color: "#e54a13" }}></i>
						Shipping & Processing
					</h6>
					<div className='row'>
						<div className='col-md-6'>
							<InfoField label='Processing Time' value={data.processingTime} />
						</div>
						<div className='col-md-6'>
							<InfoField label='Recipient Address' value={data.recipientAddress} />
						</div>
					</div>
				</div>

				{/* Attributes */}
				{data.attributes && data.attributes.length > 0 && (
					<div style={styles.section}>
						<h6 style={styles.sectionTitle}>
							<i className='bi bi-tags me-2' style={{ color: "#e54a13" }}></i>
							Attributes
						</h6>
						<div className='row'>
							{data.attributes.map((attr, i) => (
								<div className='col-md-4 col-sm-6 mb-3' key={i}>
									<div style={styles.attrCard}>
										<div style={styles.attrName}>{attr.name}</div>
										<div style={styles.attrValues}>
											{(attr.values || []).map((v, j) => (
												<span key={j} style={styles.attrBadge}>
													{v}
												</span>
											))}
										</div>
										{attr.image && (
											<img
												src={attr.image}
												alt={attr.name}
												style={styles.attrImg}
												onClick={() => setLightboxImg(attr.image)}
												onError={(e) => {
													e.target.onerror = null;
													e.target.src = defaultImage;
												}}
											/>
										)}
									</div>
								</div>
							))}
						</div>
					</div>
				)}

				{/* Seller & Category */}
				<div style={styles.section}>
					<h6 style={styles.sectionTitle}>
						<i className='bi bi-person-badge me-2' style={{ color: "#e54a13" }}></i>
						Seller & Category
					</h6>
					<div className='row'>
						<div className='col-md-4'>
							<InfoField label='Seller' value={sellerName} />
						</div>
						<div className='col-md-4'>
							<InfoField label='Category' value={categoryName} />
						</div>
						<div className='col-md-4'>
							<InfoField label='Sub Category' value={subCategoryName} />
						</div>
					</div>
				</div>

				{/* Timestamps */}
				<div style={styles.section}>
					<h6 style={styles.sectionTitle}>
						<i className='bi bi-clock-history me-2' style={{ color: "#7a7a7a" }}></i>
						Timestamps
					</h6>
					<div className='row'>
						<div className='col-md-4'>
							<InfoField label='Created At' value={data.createdAt ? new Date(data.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-"} />
						</div>
						<div className='col-md-4'>
							<InfoField label='Updated At' value={data.updatedAt ? new Date(data.updatedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-"} />
						</div>
						{data.date && (
							<div className='col-md-4'>
								<InfoField label='Date' value={data.date} />
							</div>
						)}
					</div>
				</div>
			</div>

			{/* Lightbox */}
			{lightboxImg && (
				<>
					<div style={styles.lightboxOverlay} onClick={() => setLightboxImg(null)} />
					<div style={styles.lightboxContainer} onClick={() => setLightboxImg(null)}>
						<img src={lightboxImg} alt='Full view' style={styles.lightboxImg} />
						<button style={styles.lightboxClose} onClick={() => setLightboxImg(null)}>
							<i className='bi bi-x-lg'></i>
						</button>
					</div>
				</>
			)}

			{/* Reject Reason Modal */}
			{showRejectModal && (
				<>
					<div style={styles.modalOverlay} onClick={() => setShowRejectModal(false)} />
					<div style={styles.modal}>
						<h6 style={{ margin: "0 0 16px 0", color: "#333", fontWeight: 600 }}>Reject Product</h6>
						<p style={{ color: "#666", fontSize: "14px", margin: "0 0 12px 0" }}>Please provide a reason for rejecting this product. This will be shared with the seller.</p>
						<textarea rows={4} style={styles.textarea} placeholder='Enter rejection reason...' value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} autoFocus />
						<div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "16px" }}>
							<button
								style={styles.cancelBtn}
								onClick={() => {
									setShowRejectModal(false);
									setRejectReason("");
								}}>
								Cancel
							</button>
							<button style={{ ...styles.rejectConfirmBtn, opacity: rejectReason.trim() ? 1 : 0.5 }} onClick={handleRejectConfirm} disabled={!rejectReason.trim() || actionLoading}>
								Confirm Reject
							</button>
						</div>
					</div>
				</>
			)}
		</div>
	);
};

const InfoField = ({ label, value, badge }) => (
	<div style={{ marginBottom: "12px" }}>
		<label style={styles.fieldLabel}>{label}</label>
		{badge ? (
			<span
				style={{
					display: "inline-block",
					padding: "2px 10px",
					borderRadius: "4px",
					fontSize: "13px",
					fontWeight: 600,
					backgroundColor: value === "Pending" ? "rgba(255,193,7,0.12)" : value === "Approved" ? "rgba(40,167,69,0.12)" : value === "Rejected" ? "rgba(220,53,69,0.12)" : "transparent",
					color: value === "Pending" ? "#d4a017" : value === "Approved" ? "#1e7e34" : value === "Rejected" ? "#c82333" : "#333",
				}}>
				{value || "-"}
			</span>
		) : (
			<div style={styles.fieldValue}>{value || "-"}</div>
		)}
	</div>
);

const styles = {
	page: {
		padding: "20px",
		backgroundColor: "#f4f4f8",
		minHeight: "calc(100vh - 70px)",
	},
	topBar: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		marginBottom: "16px",
		flexWrap: "wrap",
		gap: "10px",
	},
	backBtn: {
		display: "flex",
		alignItems: "center",
		background: "#fff",
		border: "1px solid #ddd",
		borderRadius: "8px",
		padding: "8px 16px",
		cursor: "pointer",
		fontSize: "14px",
		fontWeight: 500,
		color: "#333",
		transition: "all 0.2s",
	},
	pageTitle: {
		margin: 0,
		color: "#333",
		fontSize: "20px",
		fontWeight: 600,
		flex: 1,
		textAlign: "center",
	},
	actionBtns: {
		display: "flex",
		gap: "10px",
	},
	approveBtn: {
		display: "flex",
		alignItems: "center",
		background: "#2e7d32",
		color: "#fff",
		border: "none",
		borderRadius: "8px",
		padding: "8px 20px",
		cursor: "pointer",
		fontSize: "14px",
		fontWeight: 600,
		transition: "all 0.2s",
	},
	rejectBtn: {
		display: "flex",
		alignItems: "center",
		background: "#c62828",
		color: "#fff",
		border: "none",
		borderRadius: "8px",
		padding: "8px 20px",
		cursor: "pointer",
		fontSize: "14px",
		fontWeight: 600,
		transition: "all 0.2s",
	},
	statusBanner: {
		padding: "12px 20px",
		borderRadius: "8px",
		border: "1px solid",
		fontSize: "14px",
		fontWeight: 500,
		marginBottom: "16px",
	},
	rejectReasonBanner: {
		padding: "12px 20px",
		borderRadius: "8px",
		border: "1px solid #ef9a9a",
		backgroundColor: "#ffebee",
		color: "#c62828",
		fontSize: "14px",
		marginBottom: "16px",
	},
	section: {
		marginBottom: "20px",
		padding: "20px",
		backgroundColor: "#fff",
		borderRadius: "10px",
		border: "1px solid #e8e8e8",
		boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
	},
	sectionTitle: {
		color: "#333",
		fontWeight: 600,
		marginBottom: "16px",
		paddingBottom: "10px",
		borderBottom: "1px solid #f0f0f0",
		fontSize: "15px",
	},
	fieldLabel: {
		fontSize: "11px",
		fontWeight: 600,
		color: "#999",
		textTransform: "uppercase",
		letterSpacing: "0.5px",
		display: "block",
		marginBottom: "4px",
	},
	fieldValue: {
		color: "#333",
		fontSize: "14px",
		lineHeight: "1.5",
	},
	mainImageWrap: {
		textAlign: "center",
		marginBottom: "12px",
	},
	mainDisplayImg: {
		maxWidth: "100%",
		maxHeight: "400px",
		objectFit: "contain",
		borderRadius: "10px",
		cursor: "pointer",
		border: "2px solid #e8e8e8",
	},
	thumbnailRow: {
		display: "flex",
		gap: "8px",
		justifyContent: "center",
		flexWrap: "wrap",
	},
	thumbnail: {
		width: "65px",
		height: "65px",
		objectFit: "cover",
		borderRadius: "8px",
		cursor: "pointer",
		transition: "border-color 0.2s",
	},
	noImage: {
		textAlign: "center",
		padding: "60px",
		color: "#999",
		fontSize: "14px",
	},
	attrCard: {
		backgroundColor: "#f9f9fb",
		borderRadius: "8px",
		padding: "14px",
		border: "1px solid #e8e8e8",
	},
	attrName: {
		color: "#333",
		fontWeight: 600,
		fontSize: "13px",
		marginBottom: "6px",
	},
	attrValues: {
		display: "flex",
		flexWrap: "wrap",
		gap: "4px",
		marginBottom: "8px",
	},
	attrBadge: {
		display: "inline-block",
		padding: "2px 10px",
		borderRadius: "4px",
		fontSize: "12px",
		backgroundColor: "rgba(229,74,19,0.1)",
		color: "#e54a13",
	},
	attrImg: {
		width: "44px",
		height: "44px",
		borderRadius: "6px",
		objectFit: "cover",
		cursor: "pointer",
		border: "1px solid #e8e8e8",
	},
	lightboxOverlay: {
		position: "fixed",
		inset: 0,
		backgroundColor: "rgba(0,0,0,0.85)",
		zIndex: 1060,
	},
	lightboxContainer: {
		position: "fixed",
		inset: 0,
		zIndex: 1070,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		padding: "40px",
	},
	lightboxImg: {
		maxWidth: "90%",
		maxHeight: "85vh",
		objectFit: "contain",
		borderRadius: "8px",
		boxShadow: "0 10px 40px rgba(0,0,0,0.5)",
	},
	lightboxClose: {
		position: "absolute",
		top: "20px",
		right: "20px",
		background: "rgba(255,255,255,0.25)",
		border: "none",
		color: "#fff",
		width: "40px",
		height: "40px",
		borderRadius: "50%",
		fontSize: "18px",
		cursor: "pointer",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},
	// Reject Modal
	modalOverlay: {
		position: "fixed",
		inset: 0,
		backgroundColor: "rgba(0,0,0,0.5)",
		zIndex: 1080,
	},
	modal: {
		position: "fixed",
		top: "50%",
		left: "50%",
		transform: "translate(-50%, -50%)",
		backgroundColor: "#fff",
		borderRadius: "12px",
		padding: "24px",
		width: "90%",
		maxWidth: "480px",
		zIndex: 1090,
		boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
	},
	textarea: {
		width: "100%",
		borderRadius: "8px",
		border: "1px solid #ddd",
		padding: "12px",
		fontSize: "14px",
		resize: "vertical",
		fontFamily: "inherit",
		outline: "none",
		transition: "border-color 0.2s",
	},
	cancelBtn: {
		background: "#f5f5f5",
		border: "1px solid #ddd",
		borderRadius: "8px",
		padding: "8px 20px",
		cursor: "pointer",
		fontSize: "14px",
		fontWeight: 500,
		color: "#666",
	},
	rejectConfirmBtn: {
		background: "#c62828",
		color: "#fff",
		border: "none",
		borderRadius: "8px",
		padding: "8px 20px",
		cursor: "pointer",
		fontSize: "14px",
		fontWeight: 600,
	},
};

export default ProductRequestView;
